// ═══════════════════════════════════════════════════════════════════
//  groupDragMath.ts — pure delta calculation for moving an entire
//  expanded group (Roadmap follow-up: mobile group-drag jitter).
//
//  Background: WorkflowCanvas's `handleMouseMove` + the touch-path
//  `handleGroupDragMoveFromCoords` both compute "where should each
//  block go now?" from:
//    - the group's blockIds
//    - the per-block initial positions captured at drag-start
//    - the start-mouse coordinate (canvas-inner space) captured at start
//    - the current pointer coordinate (canvas-inner space)
//
//  Extracting the math here means we can unit-test the contract —
//  in particular that *every* block in the group gets a delta-updated
//  position when initial-positions are present, even on the first
//  frame after drag-start (the original mobile-jitter bug shipped
//  empty `{}` for initial-positions on frame 0 and silently dropped
//  every update).
// ═══════════════════════════════════════════════════════════════════

export interface GroupDragInitialPositions {
  [blockId: string]: { x: number; y: number };
}

export interface GroupDragInput {
  groupBlockIds: readonly string[];
  initialPositions: GroupDragInitialPositions;
  startMouse: { x: number; y: number };
  current: { x: number; y: number };
  /** Optional pre-resolved sibling-overlap adjustment. */
  overlapAdjust?: { dx: number; dy: number };
}

export interface GroupDragUpdate {
  blockId: string;
  x: number;
  y: number;
}

/**
 * Compute the per-block target position for the current pointer
 * coordinate, given the initial-position map captured at drag-start.
 *
 * Skips any block whose initial position is missing — this is the
 * "frame 0" guard that the React-state-driven implementation
 * accidentally hit on every touchmove because state hadn't committed
 * yet. Callers that switched to refs always pass a populated map.
 */
export function computeGroupDragUpdates(input: GroupDragInput): GroupDragUpdate[] {
  const deltaX = input.current.x - input.startMouse.x;
  const deltaY = input.current.y - input.startMouse.y;
  const adjDx = input.overlapAdjust?.dx ?? 0;
  const adjDy = input.overlapAdjust?.dy ?? 0;
  const out: GroupDragUpdate[] = [];
  for (const blockId of input.groupBlockIds) {
    const init = input.initialPositions[blockId];
    if (!init) continue;
    out.push({
      blockId,
      x: init.x + deltaX + adjDx,
      y: init.y + deltaY + adjDy,
    });
  }
  return out;
}

/**
 * Compute the proposed group bounds for sibling-overlap resolution.
 * Mirrors the inline math in WorkflowCanvas (±100 X pad, ±50 Y pad).
 */
export function proposedGroupBounds(
  input: Pick<GroupDragInput, 'groupBlockIds' | 'initialPositions' | 'startMouse' | 'current'>,
  pad: { x: number; y: number } = { x: 100, y: 50 },
): { left: number; top: number; right: number; bottom: number } | null {
  const deltaX = input.current.x - input.startMouse.x;
  const deltaY = input.current.y - input.startMouse.y;
  const xs: number[] = [];
  const ys: number[] = [];
  for (const blockId of input.groupBlockIds) {
    const init = input.initialPositions[blockId];
    if (!init) continue;
    xs.push(init.x + deltaX);
    ys.push(init.y + deltaY);
  }
  if (xs.length === 0) return null;
  return {
    left: Math.min(...xs) - pad.x,
    top: Math.min(...ys) - pad.y,
    right: Math.max(...xs) + pad.x,
    bottom: Math.max(...ys) + pad.y,
  };
}
